import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../../api/axios';

const initialState = {
    bookingPendingCount: 0,
    requestNeedReplyCount: 0,

    loading: false,
    error: null,

    lastFetchedAt: null,
};

const extractErrorMessage = (error) => {
    const data = error?.response?.data;

    if (typeof data === 'string') return data;
    if (Array.isArray(data?.message)) return data.message.join(', ');
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
    if (error?.message) return error.message;

    return 'Failed to load action counts';
};

export const fetchActionCounts = createAsyncThunk(
    'actionCounts/fetchActionCounts',
    async (_, { rejectWithValue }) => {
        try {
            const response = await API.get('/dashboard/action-counts');

            return {
                bookingPendingCount:
                    Number(response.data?.bookingPendingCount) || 0,

                requestNeedReplyCount:
                    Number(response.data?.requestNeedReplyCount) || 0,
            };
        } catch (error) {
            return rejectWithValue(
                extractErrorMessage(error)
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const state = getState().actionCounts;

            if (!state) {
                return true;
            }

            // 已经有请求在进行，不重复请求
            if (state.loading) {
                return false;
            }

            // 30 秒简单缓存
            if (
                state.lastFetchedAt &&
                Date.now() - state.lastFetchedAt < 30000
            ) {
                return false;
            }

            return true;
        },
    }
);

const actionCountsSlice = createSlice({
    name: 'actionCounts',

    initialState,

    reducers: {
        /**
         * 登录退出或需要手动清理时调用。
         */
        resetActionCounts: () => initialState,

        /**
         * 使缓存立即失效。
         * 下一次 fetchActionCounts() 会真正请求服务器。
         */
        invalidateActionCounts: (state) => {
            state.lastFetchedAt = null;
        },

        /**
         * 有时候操作成功后可以直接减 1，
         * 不必等待重新请求。
         *
         * 目前不强制使用，保留给以后。
         */
        decrementBookingPendingCount: (state) => {
            state.bookingPendingCount = Math.max(
                0,
                state.bookingPendingCount - 1
            );
        },

        decrementRequestNeedReplyCount: (state) => {
            state.requestNeedReplyCount = Math.max(
                0,
                state.requestNeedReplyCount - 1
            );
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchActionCounts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(
                fetchActionCounts.fulfilled,
                (state, action) => {
                    state.loading = false;

                    state.bookingPendingCount =
                        action.payload.bookingPendingCount;

                    state.requestNeedReplyCount =
                        action.payload.requestNeedReplyCount;

                    state.lastFetchedAt = Date.now();
                }
            )

            .addCase(
                fetchActionCounts.rejected,
                (state, action) => {
                    state.loading = false;

                    // 如果 condition 阻止了 thunk，
                    // Redux Toolkit 可能产生 condition rejected。
                    // 不需要把它显示成真正错误。
                    if (action.meta?.condition) {
                        return;
                    }

                    state.error =
                        typeof action.payload === 'string'
                            ? action.payload
                            : 'Failed to load action counts';
                }
            );
    },
});

export const {
    resetActionCounts,
    invalidateActionCounts,
    decrementBookingPendingCount,
    decrementRequestNeedReplyCount,
} = actionCountsSlice.actions;

export default actionCountsSlice.reducer;